import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { newsCategoryOptions, newsList } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_STEP = 5;

export default function NewsPage() {
  const [category, setCategory] = useState("全部");
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);

  const filtered = useMemo(
    () => (category === "全部" ? newsList : newsList.filter((article) => article.category === category)),
    [category],
  );
  const visible = filtered.slice(0, visibleCount);
  const featured = filtered[0];
  const rest = visible.filter((article) => article.id !== featured?.id);
  const hasMore = visibleCount < filtered.length;

  usePageMeta({
    title: "影视资讯 - 影视新闻与幕后揭秘 - “奸情”影视内容站",
    description: `整理最新影视资讯、幕后揭秘与发行动态，共 ${newsList.length} 篇深度内容，帮助你了解剧集背后的制作故事与行业变化。`,
    keywords: "影视资讯,影视新闻,幕后揭秘,发行动态,奸情影视",
  });

  const changeCategory = (value: string) => {
    setCategory(value);
    setVisibleCount(PAGE_STEP);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-10 pt-24 md:px-8 md:pb-14 md:pt-32 lg:px-12">
          <div className="w-full">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "资讯栏目" }]} />
            <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <h1 className="font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
                  影视资讯
                </h1>
                <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-[15px]">
                  发行动态、幕后揭秘与剧情讨论。我们关注剧集如何被拍出来，也关注它为什么值得被讨论。
                </p>
              </div>
              <p className="text-xs text-foreground-500">
                共 <span className="font-label font-bold text-primary-800">{filtered.length}</span> 篇 · 每周更新
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-8 md:px-8 md:py-12 lg:px-12">
          <div className="flex flex-wrap items-center gap-2 border-b border-background-200 pb-5">
            {newsCategoryOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => changeCategory(option)}
                className={`rounded-full px-3.5 py-2 text-xs font-medium transition-colors whitespace-nowrap md:text-sm ${
                  category === option
                    ? "bg-primary-900 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
                }`}
                aria-pressed={category === option}
              >
                {option === "全部" ? "全部资讯" : option}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-2xl text-foreground-400">
                <i className="ri-newspaper-line" aria-hidden="true" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">该分类下暂时没有资讯</h2>
              <p className="mt-2 text-sm text-foreground-500">换个分类看看，或者回到全部资讯浏览。</p>
              <button
                type="button"
                onClick={() => changeCategory("全部")}
                className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-900 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors whitespace-nowrap hover:bg-primary-800"
              >
                <i className="ri-refresh-line text-base" aria-hidden="true" />
                查看全部资讯
              </button>
            </div>
          ) : (
            <>
              {featured && (
                <Link
                  to={`/news/${featured.id}`}
                  className="group mt-8 grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 lg:grid-cols-2"
                >
                  <div className="relative aspect-[16/9] w-full overflow-hidden bg-secondary-900 lg:aspect-auto lg:h-full lg:min-h-[280px]">
                    <img
                      src={featured.cover}
                      alt={`${featured.title}配图`}
                      title={`${featured.title} - 影视资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-5 md:p-8">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
                        {featured.category}
                      </span>
                      <span className="font-label text-xs text-foreground-400">{featured.date}</span>
                    </div>
                    <h2 className="mt-3 font-heading text-xl font-black leading-8 text-foreground-950 transition-colors group-hover:text-primary-700 md:text-2xl">
                      {featured.title}
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-foreground-600 line-clamp-3">{featured.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700">
                      阅读全文
                      <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              )}

              <div className="mt-4 flex flex-col divide-y divide-background-200">
                {rest.map((article) => (
                  <Link
                    key={article.id}
                    to={`/news/${article.id}`}
                    className="group flex flex-col gap-4 py-5 transition-colors md:flex-row md:items-center md:gap-6"
                  >
                    <div className="h-[180px] w-full shrink-0 overflow-hidden rounded-lg bg-secondary-900 md:h-[130px] md:w-[220px]">
                      <img
                        src={article.cover}
                        alt={`${article.title}配图`}
                        title={`${article.title} - 影视资讯`}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-secondary-900">
                          {article.category}
                        </span>
                        <span className="font-label text-foreground-400">{article.date}</span>
                        <span className="text-foreground-400">· {article.author}</span>
                      </div>
                      <h3 className="mt-2 font-heading text-base font-bold leading-7 text-foreground-900 transition-colors group-hover:text-primary-700 md:text-lg">
                        {article.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-6 text-foreground-500 line-clamp-2">{article.summary}</p>
                    </div>
                    <i
                      className="ri-arrow-right-line hidden shrink-0 text-xl text-foreground-300 transition-colors group-hover:text-accent-500 md:block"
                      aria-hidden="true"
                    />
                  </Link>
                ))}
              </div>

              <div className="mt-8 flex flex-col items-center gap-3 border-t border-background-200 pt-8">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((prev) => prev + PAGE_STEP)}
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors whitespace-nowrap hover:border-primary-300 hover:text-primary-700"
                  >
                    加载更多资讯
                    <i className="ri-arrow-down-line text-base" aria-hidden="true" />
                  </button>
                ) : (
                  <p className="text-xs text-foreground-400">已经看到全部 {filtered.length} 篇资讯了</p>
                )}
              </div>
            </>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}